import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  PlusIcon, 
  FunnelIcon, 
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  TicketIcon
} from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import { backendAPI } from '@/utils/api'
import { 
  Ticket, 
  TicketStatus, 
  Priority, 
  TicketFilters,
  SLAStatus,
  TicketStats 
} from '@/types'

const TicketsPage: React.FC = () => {
  const [filters, setFilters] = useState<TicketFilters>({})
  const [search, setSearch] = useState('')
  const [selectedTickets, setSelectedTickets] = useState<string[]>([])
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy] = useState('created_at')
  const [sortOrder] = useState<'asc' | 'desc'>('desc')

  // Fetch tickets
  const { 
    data: ticketsResponse, 
    isLoading: ticketsLoading, 
    error: ticketsError,
    refetch: refetchTickets 
  } = useQuery({
    queryKey: ['tickets', filters, search, currentPage, sortBy, sortOrder],
    queryFn: () => backendAPI.getTickets({
      ...filters,
      search,
      page: currentPage,
      limit: 25,
      sortBy,
      sortOrder
    }),
  })

  // Fetch ticket statistics
  const { data: statsResponse } = useQuery({
    queryKey: ['ticket-stats'],
    queryFn: () => backendAPI.getTicketStats('30d'),
  })

  const tickets = ticketsResponse?.data?.tickets || []
  const pagination = ticketsResponse?.data?.pagination
  const stats: TicketStats = statsResponse?.data || {
    totalTickets: 0,
    openTickets: 0,
    resolvedTickets: 0,
    slaBreached: 0,
    timeframe: '30d'
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  const handleFilterChange = (newFilters: Partial<TicketFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setFilters({})
    setSearch('')
    setCurrentPage(1)
  }

  const handleBulkStatusUpdate = async (status: TicketStatus) => {
    if (selectedTickets.length === 0) return

    try {
      await Promise.all(
        selectedTickets.map(ticketId => 
          backendAPI.updateTicket(ticketId, { status })
        )
      )
      toast.success(`Updated ${selectedTickets.length} tickets`)
      setSelectedTickets([])
      refetchTickets()
    } catch (error) {
      toast.error('Failed to update tickets')
    }
  }

  const getStatusColor = (status: TicketStatus): string => {
    const colors = {
      OPEN: 'bg-blue-100 text-blue-800 border-blue-200',
      IN_PROGRESS: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      WAITING_FOR_CLIENT: 'bg-purple-100 text-purple-800 border-purple-200',
      WAITING_FOR_AGENT: 'bg-orange-100 text-orange-800 border-orange-200',
      ESCALATED: 'bg-red-100 text-red-800 border-red-200',
      RESOLVED: 'bg-green-100 text-green-800 border-green-200',
      CLOSED: 'bg-gray-100 text-gray-800 border-gray-200',
      REOPENED: 'bg-indigo-100 text-indigo-800 border-indigo-200'
    }
    return colors[status] || colors.OPEN
  }

  const getPriorityColor = (priority: Priority): string => {
    const colors: Record<Priority, string> = {
      LOW: 'text-green-600',
      MEDIUM: 'text-yellow-600',
      HIGH: 'text-orange-600',
      URGENT: 'text-red-600'
    }
    return colors[priority] || colors.MEDIUM
  }

  const getSLAStatusIcon = (slaStatus: SLAStatus) => {
    switch (slaStatus) {
      case 'OK':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />
      case 'WARNING':
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
      case 'BREACHED':
        return <XCircleIcon className="h-4 w-4 text-red-500" />
      default:
        return <ClockIcon className="h-4 w-4 text-gray-400" />
    }
  }

  const formatTimeRemaining = (dueDate?: string): string => {
    if (!dueDate) return ''
    
    const now = new Date()
    const due = new Date(dueDate)
    const diff = due.getTime() - now.getTime()
    
    if (diff < 0) return 'Overdue'
    
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)
    
    if (days > 0) return `${days}d ${hours % 24}h`
    return `${hours}h`
  }

  if (ticketsError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading tickets</h3>
          <p className="mt-1 text-sm text-gray-500">Please try refreshing the page</p>
          <button 
            onClick={() => refetchTickets()}
            className="mt-4 btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Tickets
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage support tickets and customer requests
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <Link
            to="/tickets/new"
            className="btn-primary inline-flex items-center gap-x-1.5"
          >
            <PlusIcon className="-ml-0.5 h-5 w-5" />
            New Ticket
          </Link>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TicketIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Tickets
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.totalTickets}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Open Tickets
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.openTickets}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Resolved This Month
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.resolvedTickets}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    SLA Breached
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.slaBreached}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Search tickets by subject, description, or ticket number..."
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <FunnelIcon className="h-5 w-5 mr-2" />
              Filters
              <ChevronDownIcon className={`ml-2 h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-4 border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    value={filters.status?.[0] || ''}
                    onChange={(e) => handleFilterChange({ 
                      status: e.target.value ? [e.target.value as TicketStatus] : undefined 
                    })}
                  >
                    <option value="">All Statuses</option>
                    <option value="OPEN">Open</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="WAITING_FOR_CLIENT">Waiting for Client</option>
                    <option value="WAITING_FOR_AGENT">Waiting for Agent</option>
                    <option value="ESCALATED">Escalated</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="CLOSED">Closed</option>
                  </select>
                </div>

                {/* Priority Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    value={filters.priority?.[0] || ''}
                    onChange={(e) => handleFilterChange({ 
                      priority: e.target.value ? [e.target.value as Priority] : undefined 
                    })}
                  >
                    <option value="">All Priorities</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>

                {/* SLA Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SLA Status
                  </label>
                  <select
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    value={filters.slaStatus?.[0] || ''}
                    onChange={(e) => handleFilterChange({ 
                      slaStatus: e.target.value ? [e.target.value as SLAStatus] : undefined 
                    })}
                  >
                    <option value="">All SLA Status</option>
                    <option value="OK">On Track</option>
                    <option value="WARNING">Warning</option>
                    <option value="BREACHED">Breached</option>
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <button
                    onClick={handleClearFilters}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedTickets.length > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm font-medium text-primary-900">
                {selectedTickets.length} ticket(s) selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkStatusUpdate('IN_PROGRESS')}
                className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-md hover:bg-yellow-200"
              >
                Mark In Progress
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('RESOLVED')}
                className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-md hover:bg-green-200"
              >
                Mark Resolved
              </button>
              <button
                onClick={() => setSelectedTickets([])}
                className="px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-200"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tickets Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {ticketsLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-sm text-gray-500">Loading tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="p-8 text-center">
            <TicketIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || Object.keys(filters).length > 0
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first ticket'
              }
            </p>
            {!search && Object.keys(filters).length === 0 && (
              <div className="mt-6">
                <Link
                  to="/tickets/new"
                  className="btn-primary"
                >
                  <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                  New Ticket
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={selectedTickets.length === tickets.length}
                        onChange={(e) => {
                                                   if (e.target.checked) {
                           setSelectedTickets(tickets.map((t: Ticket) => t.id))
                         } else {
                           setSelectedTickets([])
                         }
                        }}
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ticket
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SLA
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tickets.map((ticket: Ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={selectedTickets.includes(ticket.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTickets([...selectedTickets, ticket.id])
                            } else {
                              setSelectedTickets(selectedTickets.filter(id => id !== ticket.id))
                            }
                          }}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <Link
                            to={`/tickets/${ticket.id}`}
                            className="text-sm font-medium text-primary-600 hover:text-primary-900"
                          >
                            {ticket.ticketNumber}
                          </Link>
                          <div className="text-sm text-gray-900 mt-1">{ticket.subject}</div>
                          {ticket.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {ticket.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                  {tag}
                                </span>
                              ))}
                              {ticket.tags.length > 3 && (
                                <span className="text-xs text-gray-500">
                                  +{ticket.tags.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                                                 {ticket.assignedTo ? (
                           <div className="flex items-center">
                             {(ticket.assignedTo.avatar_url || ticket.assignedTo.avatar) ? (
                               <img
                                 className="h-6 w-6 rounded-full mr-2"
                                 src={ticket.assignedTo.avatar_url || ticket.assignedTo.avatar}
                                 alt=""
                               />
                             ) : (
                               <div className="h-6 w-6 rounded-full bg-gray-300 mr-2" />
                             )}
                             <span className="text-sm text-gray-900">
                               {ticket.assignedTo.first_name || ticket.assignedTo.firstName} {ticket.assignedTo.last_name || ticket.assignedTo.lastName}
                             </span>
                           </div>
                        ) : (
                          <span className="text-sm text-gray-500">Unassigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {ticket.client ? (
                          <span className="text-sm text-gray-900">{ticket.client.name}</span>
                        ) : (
                          <span className="text-sm text-gray-500">No client</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getSLAStatusIcon(ticket.slaStatus)}
                          <span className="ml-2 text-sm text-gray-900">
                            {ticket.responseDueAt && formatTimeRemaining(ticket.responseDueAt)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                    disabled={currentPage === pagination.totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">
                        {((currentPage - 1) * pagination.limit) + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * pagination.limit, pagination.total)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{pagination.total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const page = i + 1
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === currentPage
                                ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      })}
                      <button
                        onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                        disabled={currentPage === pagination.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default TicketsPage